import { useMutation, useQueryClient } from '@tanstack/react-query';
import { availableIn, capabilityOf, planName, TOOL_IDS, TOOLS, toolName, type ToolId } from '@dale/plans';

import { ApiError } from '../api/client';
import { ME_QUERY_KEY } from '../api/endpoints/permissions';
import { setTool } from '../api/endpoints/tools';
import type { BusinessMeResponse, ToolMap } from '../api/schemas';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { LockGlyph } from '../components/PlanLocked';
import { Switch } from '../features/permissions/Switch';
import { meetsPlan } from '../lib/plan';
import { useBusiness } from '../lib/useBusiness';
import { useTools } from '../lib/useTools';

/**
 * Ajustes, Herramientas: every business type gets what a class studio gets
 * (the Calendario, planes or packages, spaces and group sessions), named in
 * the trade's words and on by the type's default. The owner shows or hides
 * each one for the whole team. A tool the plan does not include can still be
 * shown: its door carries the lock and the plan that opens it. Nothing shows
 * until the business and its tools are known, so no guessed name flashes.
 */
export function HerramientasPage() {
  const queryClient = useQueryClient();
  const business = useBusiness();
  const { tools, category, ready } = useTools();
  const mutation = useMutation({
    mutationFn: ({ tool, enabled }: { tool: ToolId; enabled: boolean }) => setTool(tool, enabled),
    // The switch already shows the change; /me carries it to the nav without a refetch.
    onSuccess: (next: ToolMap) =>
      queryClient.setQueryData<BusinessMeResponse>(ME_QUERY_KEY, (current) =>
        current ? { ...current, data: { ...current.data, tools: next } } : current,
      ),
  });
  const shown = mutation.isPending && mutation.variables ? { ...tools, [mutation.variables.tool]: mutation.variables.enabled } : tools;

  return (
    <main className="shell-main">
      <PageHeader title="Herramientas" description="Lo que usas de Dale en tu negocio. Enciende lo que te sirve; lo demás no estorba." />
      {ready ? (
        <ul className="tool-list">
          {TOOL_IDS.map((tool) => {
            const name = category ? toolName(tool, category) : TOOLS[tool].otherwise;
            const capability = TOOLS[tool].capability;
            const lockedBy = capability && business && !meetsPlan(business.plan, capabilityOf(capability).minPlan) ? capability : undefined;
            return (
              <li key={tool} className="tool-row">
                <div className="tool-text">
                  <p className="tool-name" id={`tool-${tool}`}>
                    {name}
                  </p>
                  <p className="tool-blurb" id={`tool-${tool}-blurb`}>
                    {TOOLS[tool].blurb}
                  </p>
                  {lockedBy ? (
                    <p className="tool-plan">
                      <LockGlyph />
                      <span>{availableIn(lockedBy)}</span>
                      <span className="sr-only">, hoy lo verías con candado hasta {planName(capabilityOf(lockedBy).minPlan)}</span>
                    </p>
                  ) : null}
                </div>
                <Switch
                  checked={shown[tool]}
                  onChange={(enabled) => mutation.mutate({ tool, enabled })}
                  labelledBy={`tool-${tool}`}
                  describedBy={`tool-${tool}-blurb`}
                  disabled={mutation.isPending}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <LoadingState label="Cargando tus herramientas…" />
      )}
      {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el cambio. Intenta de nuevo en un momento.';
}
